/**
 * English comment normalized.
 * English comment normalized.
 */

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import { uppKonvaAreaWire } from '../plan/adapters/floor-plan-from-area-source';
import type { UppKonvaAreaSource } from '../../model/upp-konva-source';
import type { UppFloorPlanModel as UppFloorPlanSceneModel } from '../plan/floor-plan-model';
import type { UppFloorPlanPreferences } from '../plan/floor-plan-preferences';
import {
  mergeUppFloorPlanTheme,
  UPPKONVA_DARKTHEME,
  UPPKONVA_LIGHTTHEME,
  type UppFloorPlanTheme,
} from '../plan/floor-plan-theme';
import type { UppKonvaRefresh } from '../../model/upp-konva-refresh';
import type { UppKonvaTheme } from '../../model/upp-konva-types';
import {
  UppFloorPlanStageComponent,
  UppKonvaFloorPlanModel,
  type UppFloorPlanElementClickPayload,
  type UppFloorPlanElementGeometryPayload,
  type UppFloorPlanRoomBackgroundPointerPayload,
  type UppFloorPlanVertexDoubleClickPayload,
  type UppKonvaTransform,
  uppKonvaSceneModelHost,
} from '../upp-konva-stage-api';
import { resolveUppKonvaHostTheme, uppKonvaHostStageRoomBackgroundPointerMode } from './upp-konva-host-area';
import { UppKonvaHostDrawIntegration } from './upp-konva-host-draw';
import { UppKonvaHostModelIntegration } from './upp-konva-host-model-integration';
import { emitUppKonvaGroupedFromStage } from './upp-konva-host-group';

@Component({
  selector: 'upp-konva-floor-plan',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './upp-konva-floor-plan-host.component.html',
  styleUrls: ['./upp-konva-floor-plan-host.component.scss'],
})
export class UppKonvaFloorPlanHostComponent implements AfterViewInit, OnChanges, OnDestroy {
  /** @public */
  @Input({ required: true }) model!: UppKonvaFloorPlanModel;

  /** @public */
  @Input() active = true;

  /**
   * @public Preset (`UPPKONVA_*`) or partial/complete override of the canvas theme; **`undefined`** delegates to {@link UppKonvaFloorPlanModel.theme}.
   */
  @Input() theme: UppKonvaTheme | Partial<UppFloorPlanTheme> | null | undefined;

  /** English comment normalized. */
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() readonly onChange = new EventEmitter<UppKonvaAreaSource>();

  /**
   * English comment normalized.
   * English comment normalized.
   */
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() readonly onSelect = new EventEmitter<readonly string[]>();

  /**
   * VIEW-mode zoom/pan after user navigation (wheel, pinch, background drag, or stage zoom API).
   */
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() readonly onTransform = new EventEmitter<UppKonvaTransform>();

  @ViewChild('floor', { static: false }) private _floorRef!: UppFloorPlanStageComponent;

  private readonly _modelIntegration = new UppKonvaHostModelIntegration();
  private readonly _drawIntegration = new UppKonvaHostDrawIntegration();

  private _integratorHooked: UppKonvaFloorPlanModel | null = null;
  private _savedIntegratorHost: UppKonvaRefresh | null = null;

  /**
   * Stable reference to **`upp-floor-plan-stage`** (`[theme]`). The getter **`mergeUppFloorPlanTheme(...)`**
   * English comment normalized.
   * English comment normalized.
   */
  stageMergedTheme: UppFloorPlanTheme = UPPKONVA_LIGHTTHEME;

  constructor(private readonly _cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['model']) {
      const prev = changes['model'].previousValue as UppKonvaFloorPlanModel | undefined;
      const curr = changes['model'].currentValue as UppKonvaFloorPlanModel | undefined;
      if (prev && prev !== curr) {
        this._teardownIntegratorHost();
        this._modelIntegration.teardown(prev);
      }
      if (curr) {
        this._wireIntegratorHost(curr);
      }
      this._wireModelHooks();
    }
    if (changes['model']?.currentValue) {
      this._drawIntegration.seedGeometryCacheFromModel(this.model);
    }
    if (changes['model']?.currentValue || changes['theme']) {
      this._syncStageMergedTheme();
    }
    if (changes['active'] || changes['theme']) {
      this.model?.doRefresh();
    }
  }

  private _syncStageMergedTheme(): void {
    if (!this.model) {
      return;
    }
    this.stageMergedTheme = this._computeMergedTheme();
  }

  private _computeMergedTheme(): UppFloorPlanTheme {
    const t = this.theme;
    if (t === null) {
      return mergeUppFloorPlanTheme(this.model.theme ?? undefined, UPPKONVA_LIGHTTHEME);
    }
    if (t === undefined) {
      const layer = resolveUppKonvaHostTheme(undefined, this.model.theme ?? undefined);
      return mergeUppFloorPlanTheme(layer, UPPKONVA_LIGHTTHEME);
    }
    if (t === 'UPPKONVA_DARKTHEME' || t === 'UPPKONVA_LIGHTTHEME') {
      const base = t === 'UPPKONVA_DARKTHEME' ? UPPKONVA_DARKTHEME : UPPKONVA_LIGHTTHEME;
      return mergeUppFloorPlanTheme(this.model.theme ?? undefined, base);
    }
    const hostLayer = mergeUppFloorPlanTheme(t, UPPKONVA_LIGHTTHEME);
    return mergeUppFloorPlanTheme(this.model.theme ?? undefined, hostLayer);
  }

  get stageRoomBackgroundPointerMode(): 'none' | 'doubleClickRoomCm' {
    return uppKonvaHostStageRoomBackgroundPointerMode(this.model);
  }

  get konvaHostSceneModel(): UppFloorPlanSceneModel {
    return uppKonvaSceneModelHost(this.model).sceneModel;
  }

  get konvaHostPresentationPreferences(): UppFloorPlanPreferences {
    return uppKonvaSceneModelHost(this.model).presentationPreferences;
  }

  get konvaHostVertexPreviewAnchorId(): string | null {
    return uppKonvaSceneModelHost(this.model).vertexPreviewAnchorId;
  }

  ngAfterViewInit(): void {
    this._wireModelHooks();
    this._drawIntegration.seedGeometryCacheFromModel(this.model);
    this._syncStageMergedTheme();
  }

  ngOnDestroy(): void {
    const hooked = this._modelIntegration.hookedModel;
    if (hooked) {
      this._modelIntegration.teardown(hooked);
    }
    this._teardownIntegratorHost();
    this.model?.doDestroy();
  }

  private _wireIntegratorHost(model: UppKonvaFloorPlanModel): void {
    if (this._integratorHooked === model) {
      return;
    }
    this._teardownIntegratorHost();
    this._savedIntegratorHost = model.uppKonvaRefresh;
    model.uppKonvaRefresh = {
      doRefresh: () => {
        this._savedIntegratorHost?.doRefresh();
        this._cdr.markForCheck();
      },
    };
    this._integratorHooked = model;
    model.doRefresh();
  }

  private _teardownIntegratorHost(): void {
    if (!this._integratorHooked) {
      return;
    }
    this._integratorHooked.uppKonvaRefresh = this._savedIntegratorHost;
    this._integratorHooked = null;
    this._savedIntegratorHost = null;
  }

  private _wireModelHooks(): void {
    this._modelIntegration.wire(this.model, {
      floor: this._floorRef,
      emitStructuralAreaToHost: () => {
        this.onChange.emit(uppKonvaAreaWire(this.model.area));
      },
      onPresentationRefresh: () => {
        this._drawIntegration.seedGeometryCacheFromModel(this.model);
        this._syncStageMergedTheme();
        this._cdr.markForCheck();
      },
      onFloorModelRefresh: (floor) => {
        this._drawIntegration.syncGeometrySnapshotsFromElements(this.model);
        floor.refreshFromModel(this.konvaHostSceneModel);
      },
      onAfterFloorWired: () => {
        emitUppKonvaGroupedFromStage(this.model, this._floorRef);
      },
    });
  }

  onFloorStageSelect(elementUuids: readonly string[]): void {
    this._drawIntegration.onSelectionChange(this.model, this._floorRef, elementUuids);
    this.onSelect.emit(elementUuids);
  }

  onActivate(p: UppFloorPlanElementClickPayload): void {
    this._drawIntegration.onActivate(this.model, p);
  }

  onRoomBackgroundDoubleClick(p: UppFloorPlanRoomBackgroundPointerPayload): void {
    this._drawIntegration.onRoomBackgroundDoubleClick(this.model, this._floorRef, p);
  }

  onRoomBackgroundSingleClick(_p: UppFloorPlanRoomBackgroundPointerPayload): void {
    this._drawIntegration.onRoomBackgroundSingleClick(this.model, _p);
  }

  onVertexDoubleClick(p: UppFloorPlanVertexDoubleClickPayload): void {
    this._drawIntegration.onVertexDoubleClick(this.model, this._floorRef, p);
  }

  onGeometryCommit(p: UppFloorPlanElementGeometryPayload): void {
    const applied = this._drawIntegration.onGeometryCommit(this.model, p);
    /** English comment normalized. */
    if (applied) {
      this.onChange.emit(uppKonvaAreaWire(this.model.area));
    }
  }
}
